import React, { useEffect, useRef, useState } from 'react';

/**
 * The "you are in Presentation Mode" pill.
 *
 * At scale 1 the mode is visually identical to normal, so without this there is
 * no way to tell it is armed — and the two states behave completely differently
 * the moment you scroll with Ctrl held. Mid-demo that reads as "the hotkey
 * didn't work", the trainer presses it again, and the next Ctrl+scroll is a real
 * browser zoom that reflows the page in front of the class.
 *
 * Shows on entry and on every zoom change, then fades. It does NOT sit on the TV
 * permanently.
 */

export const DWELL_MS = 2000;

type Props = {
  active: boolean;
  /** Only for display and for re-showing on change; placement reads --pm-* directly. */
  scale: number;
};

const PresentationModeIndicator: React.FC<Props> = ({ active, scale }) => {
  const [visible, setVisible] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Re-show on entry and on any zoom change, then start the fade timer over.
  useEffect(() => {
    if (!active) return undefined;
    setVisible(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(false), DWELL_MS);
    return () => clearTimeout(timer.current);
  }, [active, scale]);

  if (!active) return null;

  return (
    <div
      data-testid="pm-indicator"
      data-visible={String(visible)}
      aria-live="polite"
      style={{
        position: 'fixed',
        // The pill lives inside the scaled <body>, so it has to be placed in
        // CONTENT coordinates that resolve to a fixed SCREEN corner:
        // content = (screen - pan) / scale. Without the pan term it would drift
        // off with the content as she pans.
        left: 'calc((var(--pm-vw) - 16px - var(--pm-x)) / var(--pm-scale))',
        top: 'calc((16px - var(--pm-y)) / var(--pm-scale))',
        // Counter-scale so it stays one physical size at any zoom, then pull it
        // back by its own width to sit flush against the right edge.
        transform: 'scale(calc(1 / var(--pm-scale))) translateX(-100%)',
        transformOrigin: '0 0',
        zIndex: 2147483647,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        borderRadius: '9999px',
        background: 'rgba(17, 24, 39, 0.85)',
        color: '#fff',
        fontSize: '13px',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 400ms ease',
      }}
    >
      <span aria-hidden>▣</span>
      <span>Presentation Mode</span>
      <strong>{Math.round(scale * 100)}%</strong>
    </div>
  );
};

export default PresentationModeIndicator;
